// Respuesta con IA de cara al cliente — tarjeta de Configuración.
// Sólo un aprobador (staff con correo verificado CON GOOGLE de @codingcloud.online o
// @dumaxst.com, en sesión interactiva) puede guardarla: el backend lo exige y
// responde 403 a cualquier otro. El backend además sanea todo el bloque.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AiFirstReplySettings, CategoryOption, LabelOption } from "../services/serviceDeskService";
import { AI_FIRST_REPLY_MODELS, withAiFirstReplyDefaults } from "../services/aiReplyPanel";

interface Props {
  value?: Partial<AiFirstReplySettings> | null;
  labels: LabelOption[];
  categories: CategoryOption[];
  isDarkMode: boolean;
  primaryColor: string;
  saving: boolean;
  onSave: (next: AiFirstReplySettings) => void;
}

const toggleIn = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

export function AiFirstReplySettingsCard({ value, labels, categories, isDarkMode, primaryColor, saving, onSave }: Props) {
  const { t } = useTranslation("serviceDesk");
  const [draft, setDraft] = useState<AiFirstReplySettings>(() => withAiFirstReplyDefaults(value));
  const set = (patch: Partial<AiFirstReplySettings>) => setDraft((d) => ({ ...d, ...patch }));

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = `rounded-lg border px-2 py-1.5 text-sm ${isDarkMode ? "!bg-gray-800 !border-gray-600 !text-white" : "!bg-white !border-gray-300"}`;
  const chip = (active: boolean) =>
    `rounded-full px-3 py-1 text-xs ${active ? "text-white" : isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"}`;

  return (
    <div className={`rounded-xl border p-5 ${card}`} data-testid="ai-first-reply-settings">
      <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.aiReply.title")}</h2>
      <p className={`mb-2 text-xs ${muted}`}>{t("settings.aiReply.description")}</p>
      <p className="mb-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-700 dark:bg-amber-500/10">{t("settings.aiReply.noPublicKb")}</p>
      <p className={`mb-4 text-xs ${muted}`}>{t("settings.aiReply.approversOnly")}</p>

      <label className={`mb-2 flex items-center gap-2 text-sm ${text}`}>
        <input type="checkbox" checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        {t("settings.aiReply.enable")}
      </label>
      <label className={`mb-4 flex items-center gap-2 text-sm ${text}`}>
        <input
          type="checkbox"
          checked={draft.conversationEnabled}
          onChange={(e) => set({ conversationEnabled: e.target.checked })}
        />
        {t("settings.aiReply.conversationEnable")}
      </label>

      <div className="space-y-3">
        <div>
          <span className={`mb-1 block text-xs ${muted}`}>{t("settings.aiReply.persona")}</span>
          <textarea
            className={`${inputCls} h-24 w-full resize-y`}
            maxLength={2000}
            placeholder={t("settings.aiReply.personaPlaceholder")}
            value={draft.persona}
            onChange={(e) => set({ persona: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-3">
          <label className={`w-40 text-xs ${muted}`}>{t("settings.aiReply.signature")}</label>
          <input className={`${inputCls} flex-1`} maxLength={60} value={draft.signature} onChange={(e) => set({ signature: e.target.value })} />
        </div>
        <div className="flex items-center gap-3">
          <label className={`w-40 text-xs ${muted}`}>{t("settings.aiReply.model")}</label>
          <select
            className={inputCls}
            value={draft.model}
            onChange={(e) => set({ model: e.target.value as AiFirstReplySettings["model"] })}
          >
            {AI_FIRST_REPLY_MODELS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <label className={`w-40 text-xs ${muted}`}>{t("settings.aiReply.maxTurns")}</label>
          <input
            type="number"
            min={1}
            max={20}
            className={`${inputCls} w-24`}
            value={draft.conversationMaxTurns}
            onChange={(e) => set({ conversationMaxTurns: Number(e.target.value) })}
          />
        </div>
        <div className="flex items-center gap-3">
          <label className={`w-40 text-xs ${muted}`}>{t("settings.aiReply.maxCost")}</label>
          <input
            type="number"
            min={0.01}
            max={20}
            step={0.01}
            className={`${inputCls} w-24`}
            value={draft.conversationMaxCostUsd}
            onChange={(e) => set({ conversationMaxCostUsd: Number(e.target.value) })}
          />
        </div>
        <div>
          <span className={`mb-1.5 block text-xs ${muted}`}>{t("settings.aiReply.excludedLabels")}</span>
          <div className="flex flex-wrap gap-2">
            {labels.map((l) => {
              const active = draft.excludedLabels.includes(l.id);
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => set({ excludedLabels: toggleIn(draft.excludedLabels, l.id) })}
                  className={chip(active)}
                  style={active ? { backgroundColor: primaryColor } : undefined}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <span className={`mb-1.5 block text-xs ${muted}`}>{t("settings.aiReply.excludedCategories")}</span>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => {
              const active = draft.excludedCategories.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => set({ excludedCategories: toggleIn(draft.excludedCategories, c.id) })}
                  className={chip(active)}
                  style={active ? { backgroundColor: primaryColor } : undefined}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onSave(draft)}
        disabled={saving}
        className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        style={{ backgroundColor: primaryColor }}
      >
        {t("settings.aiReply.save")}
      </button>
    </div>
  );
}
